import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Link, Accordion, AccordionDetails, AccordionSummary, Grid } from "@material-ui/core"
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Version, TransportConfigurationSettings, ModemWithOrgSiteResponseDTO, LogMessageDTO, ReportedTwinDTO, DeviceTwinOrHeartbeatRequestedType } from '../generated/ApiClient'
import { ApplicationState } from '../store'
import * as R from 'ramda'
import { actionCreators as TransportActions, TransportAppState } from '../store/TransportApp'
import { actionCreators as ModemActions } from '../store/Modems'
import { Link as RouterLink } from 'react-router-dom'
import ConnectionButton from './ModemConnectionButton'
import { FirmwareUpdate, FotaBaseUrls, FotaChannels, FotaProducts, SensorViewSetupUrl } from './UpdateHelper'
import CenteredProgress from '../common/CenteredLoader'
import MUIDataTable from 'mui-datatables'
import { tableOptions } from '../common/Shared'

type TransportAppProps =
    RouteComponentProps<{}>

const versionToString = (v: Version) => `${v.major}.${v.minor}.${v.build}.${(v.revision == null || v.revision < 0) ? 0 : v.revision}`

export default function TransportApp(props: TransportAppProps) {
    const dispatch = useDispatch()
    const loading = useSelector<ApplicationState, boolean | undefined>(state => state.transportAppState?.isLoading)
    const transportState = useSelector<ApplicationState, TransportConfigurationSettings | undefined>(state => state.transportAppState?.transportConfig)
    const modems = useSelector<ApplicationState, { [key: string]: ModemWithOrgSiteResponseDTO } | undefined>(state => state.modems?.modems)
    const twins = useSelector<ApplicationState, { [key: string]: { simpleTwin?: ReportedTwinDTO | undefined; completeTwin?: unknown; } | undefined; } | undefined>(state => (state.modems?.deviceTwins))
    const logs = useSelector<ApplicationState, [string, LogMessageDTO][] | undefined>(state => state.webSocket?.messages)

    React.useEffect(() => {
        // start loading the transport config so that it is ambient across all routes
        dispatch(TransportActions.requestTransportConfig())
    }, [])

    const renderInstallationAndTroubleshooting = () => {
        return (
            <>
                <Typography>Unable to communicate with the Transport App. Ensure that it is installed and running</Typography>
                <Typography>If you need to download the latest version of the Transporter App, you can find it <Link href={SensorViewSetupUrl}>here</Link></Typography>
                <Typography>You can find Help/Documentation in the <Link href="https://www.acuitybrands.com/api/products/getasset/nlight/1468780/b712a393-d931-430a-b89f-9c1d982dbed6/sensorview-user-guide.pdf?abl_version=04%2f29%2f2021+21:38:25&DOC_Type=Other_Information" target="_newtab">SensorView Users manual</Link></Typography>
            </>
        )
    }

    const renderValidConfig = (config: TransportConfigurationSettings, modems: { [key: string]: ModemWithOrgSiteResponseDTO }) => {
        return (
            <>
                <Typography paragraph>Current Version: {versionToString(config.version)}</Typography>
                <FirmwareUpdate
                    currentAppVersion={versionToString(config.version)}
                    fotaUrl={FotaBaseUrls.Prod}
                    fotaServer={FotaChannels.Stable}
                    fotaProduct={FotaProducts.Transporter} />

                <Button component={Link} href='https://localhost:45773/api/configuration/logs' target="_blank" download>Download Logs</Button>
                <br />
                <TableContainer component={Paper}>
                    <Table className='table table-striped' aria-labelledby="tabelLabel">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Local IP</TableCell>
                                <TableCell>Remote IP</TableCell>
                                <TableCell>Modem Host Id</TableCell>
                                <TableCell>Device Id</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupAndSort(config, modems)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )
    }

    const groupAndSort = (config: TransportConfigurationSettings, modems: { [key: string]: ModemWithOrgSiteResponseDTO }) => {
        const grouped = Object.entries(R.groupBy(([ip, m]) => m.modemHostId, Object.entries(config.simulatorToRemoteIpMap)))
        // group by modem id and sort by modem first so that we show eclypses connected to their parent modem
        return grouped.flatMap(s =>
            R.sortBy(([_, k]) => k.type === 'Eclypse', s[1]).map(([ip, m]) => {
                const modem = modems[m.modemHostId]
                let eclypseLabel = undefined
                if (m.type === 'Eclypse') {
                    const twin = twins ? twins[m.modemHostId]?.simpleTwin : undefined
                    if (!twin) dispatch(ModemActions.requestTwin(m.modemHostId, DeviceTwinOrHeartbeatRequestedType._0))
                    eclypseLabel = twin && twin.gateways.find(g => m.type === 'Eclypse' && g.deviceId == m.nLightId)
                }

                if (!modem) dispatch(ModemActions.requestModem(m.modemHostId))
                return (
                    <TableRow key={m.type === 'Eclypse' ? m.nLightId : m.modemHostId}>
                        <TableCell>{m.type}</TableCell>
                        <TableCell>{m.type === 'Eclypse' ? eclypseLabel?.label : modem?.label}</TableCell>
                        <TableCell><Link target="_blank" href={`https://${ip}`}>{ip}</Link></TableCell>
                        <TableCell>{m.remoteIp}</TableCell>
                        <TableCell>{m.modemHostId}</TableCell>
                        <TableCell>{m.type === 'Eclypse' ? m.nLightId : ''}</TableCell>
                        <TableCell>
                            <Button component={RouterLink} variant='contained' size='small' to={`/modems/${m.modemHostId}`}>Details</Button>
                        </TableCell>
                        <TableCell>
                            <ConnectionButton
                                onDisconnect={function () {dispatch(TransportActions.removeIpSettings([[ip, m]]));}}
                                localIp={ip} connected={true} />
                        </TableCell>
                    </TableRow>)
            }
            ))
    }

    return (
        <>
            <Typography variant='h4' id="tabelLabel">CL<b>AIR</b>ITY&trade; Link Transporter</Typography>
            {transportState === undefined ?
                renderInstallationAndTroubleshooting() :
                !loading && modems ?
                    renderValidConfig(transportState, modems)
                    : <CenteredProgress />}

            {/* <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="diagnostics-content"
                    id="diagnostics-header"
                >
                    <Typography variant='h5'>Diagnostics</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container>
                        <Grid xs={12} direction='row' item container justify='flex-end'>
                            <Grid item>
                                <Button component={Link} href='https://localhost:45773/api/configuration/logs' target="_blank" download>Download Logs</Button>
                            </Grid>
                        </Grid>
                        <Grid xs={12} item>
                            <MUIDataTable title={"Logs"} data={logs.map(([_, m]) => m)} columns={logTableColumns()} options={tableOptions}></MUIDataTable>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion> */}

        </>
    )
}

function logTableColumns() {
    return [
        {
            name: "level",
            label: "Level",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "loggerName",
            label: "Source",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "message",
            label: "Message",
            options: {
                filter: false,
                sort: false,
            }
        },
        {
            name: "timestamp",
            label: "Time",
            options: {
                filter: false,
                sort: true,
            }
        },
        {
            name: "exceptionMessage",
            label: "Exception",
            options: {
                filter: false,
                sort: false,
            }
        }
    ]
}